/**
 * Stability-Aware Rhyme Interpretation
 * Maps rhyme distance to visual opacity based on song stability expectations (1-10 scale)
 */

import { RhymeCategory, CATEGORY_WEIGHTS } from './types';

// Re-export scheme-aware utilities from expectedPositionCalculator
export {
  getExpectedRhymePosition,
  getExpectedRhymePositionWithCustom,
  calculateDistanceFromExpected,
  calculateSchemeAwareOpacity,
  parseCustomPattern,
} from './expectedPositionCalculator';

/**
 * Song stability levels define rhyme expectations
 * Different song types have different expectations for rhyme strength
 *
 * @deprecated Use numeric stability levels (1-10) instead
 */
export enum SongStability {
  TRADITIONAL = 'TRADITIONAL',      // Expects perfect rhymes (classical, structured)
  CONTEMPORARY = 'CONTEMPORARY',    // Allows some slant rhymes (modern pop, rock)
  EXPERIMENTAL = 'EXPERIMENTAL',    // Welcomes creative rhyming (avant-garde, hip-hop)
  FREEFORM = 'FREEFORM'             // No rhyme expectations (spoken word, free verse)
}

/**
 * Stability scale configuration (1-10)
 * Each level defines expected rhyme distance and acceptable range
 *
 * Distance-based opacity formula:
 * - At or below expectedDistance: full opacity (1.0)
 * - Between expectedDistance and maxAcceptableDistance: linear interpolation to minOpacity
 * - Beyond maxAcceptableDistance: minOpacity (floor)
 */
export interface StabilityConfig {
  /** Expected number of lines between rhyming words */
  expectedDistance: number
  /** Maximum distance before opacity drops to minimum */
  maxAcceptableDistance: number
  /** Floor opacity - all labels always visible */
  minOpacity: number
}

/**
 * Stability scale from 1 (very tight rhyme scheme) to 10 (very loose/experimental)
 * Single source of truth for stability configuration
 */
export const STABILITY_SCALE: Record<number, StabilityConfig> = {
  1: { expectedDistance: 1, maxAcceptableDistance: 2, minOpacity: 0.4 },   // Very tight (AABB)
  2: { expectedDistance: 1, maxAcceptableDistance: 3, minOpacity: 0.4 },
  3: { expectedDistance: 2, maxAcceptableDistance: 4, minOpacity: 0.4 },   // Couplets
  4: { expectedDistance: 2, maxAcceptableDistance: 5, minOpacity: 0.35 },
  5: { expectedDistance: 3, maxAcceptableDistance: 6, minOpacity: 0.35 },  // Default/medium (ABAB)
  6: { expectedDistance: 3, maxAcceptableDistance: 8, minOpacity: 0.35 },
  7: { expectedDistance: 4, maxAcceptableDistance: 10, minOpacity: 0.3 }, // Loose structure
  8: { expectedDistance: 5, maxAcceptableDistance: 12, minOpacity: 0.3 },
  9: { expectedDistance: 6, maxAcceptableDistance: 15, minOpacity: 0.3 }, // Very loose
  10: { expectedDistance: 8, maxAcceptableDistance: 20, minOpacity: 0.3 }, // Experimental/freeform
}

/**
 * Visual encoding channels for rhyme labels
 * Combines multiple design dimensions to communicate rhyme quality and fit
 */
export interface VisualEncoding {
  letter: string;                    // A, B, C, etc. (grouping identifier)
  callbackSubscript?: number;        // 1, 2, 3 for A₁, A₂, A₃ (cross-section callbacks)
  borderStyle: 'solid' | 'dashed' | 'dotted';  // Category strength indicator
  fontWeight: 400 | 500 | 600 | 700;  // Weight tier indicator
  opacity: number;                   // 0.4-1.0 based on stability expectation match
  colorToken: string;                // Design token reference for letter color
}

/**
 * Letter assignment state for tracking rhyme groups across sections
 */
export interface LetterGroup {
  letter: string;
  lastSectionIndex: number;
  sectionType: string;
}

/**
 * @deprecated - Legacy categorical opacity mapping (replaced by distance-based system)
 *
 * Stability expectations matrix (SPEC VALUES - DO NOT MODIFY)
 * Source: requirements.md lines 115-151
 *
 * CRITICAL: These values are from the canonical spec and must match exactly.
 * Different stability levels expect different rhyme categories.
 *
 * NOTE: This is retained only for backward compatibility with legacy tests.
 * New code should use calculateDistanceBasedOpacity() instead.
 */
const STABILITY_OPACITY_MAP: Record<SongStability, Record<RhymeCategory, number>> = {
  [SongStability.TRADITIONAL]: {
    [RhymeCategory.PERFECT]: 1.0,         // Fully expected
    [RhymeCategory.FAMILY_VOICED]: 1.0,   // Fully expected
    [RhymeCategory.FAMILY_UNVOICED]: 0.7, // Acceptable
    [RhymeCategory.ADDITIVE]: 0.5,        // Pushing it
    [RhymeCategory.SUBTRACTIVE]: 0.5,     // Pushing it
    [RhymeCategory.ASSONANCE]: 0.3,       // Too loose
    [RhymeCategory.CONSONANCE]: 0.3,      // Too loose
    [RhymeCategory.NONE]: 0.3,            // No rhyme - minimal opacity
  },
  [SongStability.CONTEMPORARY]: {
    [RhymeCategory.PERFECT]: 0.8,         // Slightly cliché
    [RhymeCategory.FAMILY_VOICED]: 1.0,   // Sweet spot
    [RhymeCategory.FAMILY_UNVOICED]: 1.0, // Sweet spot
    [RhymeCategory.ADDITIVE]: 1.0,        // Sweet spot
    [RhymeCategory.SUBTRACTIVE]: 1.0,     // Sweet spot
    [RhymeCategory.ASSONANCE]: 0.7,       // Creative choice
    [RhymeCategory.CONSONANCE]: 0.5,      // Pushing it
    [RhymeCategory.NONE]: 0.3,            // No rhyme - minimal opacity
  },
  [SongStability.EXPERIMENTAL]: {
    [RhymeCategory.PERFECT]: 0.5,         // Too on-the-nose
    [RhymeCategory.FAMILY_VOICED]: 0.7,   // Still safe
    [RhymeCategory.FAMILY_UNVOICED]: 0.8, // Acceptable
    [RhymeCategory.ADDITIVE]: 0.9,        // Good
    [RhymeCategory.SUBTRACTIVE]: 0.9,     // Good
    [RhymeCategory.ASSONANCE]: 1.0,       // Perfect!
    [RhymeCategory.CONSONANCE]: 1.0,      // Perfect!
    [RhymeCategory.NONE]: 0.3,            // Intentional choice - minimal opacity
  },
  [SongStability.FREEFORM]: {
    [RhymeCategory.PERFECT]: 1.0,         // All good
    [RhymeCategory.FAMILY_VOICED]: 1.0,
    [RhymeCategory.FAMILY_UNVOICED]: 1.0,
    [RhymeCategory.ADDITIVE]: 1.0,
    [RhymeCategory.SUBTRACTIVE]: 1.0,
    [RhymeCategory.ASSONANCE]: 1.0,
    [RhymeCategory.CONSONANCE]: 1.0,
    [RhymeCategory.NONE]: 0.3,            // No rhyme - minimal opacity
  },
};

/**
 * Letter color mapping (rotates through design token colors)
 *
 * DESIGN TOKEN REFERENCE: docs/design/feedback_system/design_tokens.json
 *
 * These hex values are derived from the design token color palette.
 * The first four colors (primary, creative, success, warm) map directly to
 * token values. Additional colors provide variety for overflow cases.
 *
 * Design token mapping:
 * - #3b82f6 → color.primary.500 (blue)
 * - #a855f7 → color.creative.500 (purple)
 * - #10b981 → color.success.500 (green)
 * - #f59e0b → color.warm.500 (amber)
 *
 * Overflow colors (not in design tokens, added for variety):
 * - #ef4444 → red (for 5th+ rhyme group)
 * - #06b6d4 → cyan
 * - #ec4899 → pink
 * - #8b5cf6 → violet
 *
 * NOTE: Hardcoded hex values used for runtime performance. If design tokens
 * change, update these values and this comment to maintain consistency.
 */
const LETTER_COLORS = [
  '#3b82f6', // primary.500 (blue)
  '#a855f7', // creative.500 (purple)
  '#10b981', // success.500 (green)
  '#f59e0b', // warm.500 (amber)
  '#ef4444', // red (overflow)
  '#06b6d4', // cyan (overflow)
  '#ec4899', // pink (overflow)
  '#8b5cf6', // violet (overflow)
];

/**
 * @deprecated - Use calculateDistanceBasedOpacity() instead
 *
 * Interpret stability expectations for a rhyme category
 * Returns opacity and whether the category matches expectations
 *
 * This function is part of the legacy categorical system. New code should use
 * the distance-based opacity calculation which provides finer-grained control.
 *
 * @param category - The rhyme category detected
 * @param stability - The song's stability level
 */
export function interpretStability(
  category: RhymeCategory,
  stability: SongStability
): { opacity: number; isExpected: boolean } {
  const opacity = STABILITY_OPACITY_MAP[stability][category];

  // Expected if opacity >= 0.8 (arbitrary threshold for "expected")
  const isExpected = opacity >= 0.8;

  return { opacity, isExpected };
}

/**
 * @deprecated - No longer used in distance-based system
 *
 * Get grouping boundary threshold based on song stability
 *
 * Determines the minimum rhyme weight required for letter assignment.
 * Different stability levels have different expectations for rhyme strength.
 *
 * Thresholds:
 * - TRADITIONAL/CONTEMPORARY: 0.70 (only strong rhymes: PERFECT, FAMILY, ADDITIVE, SUBTRACTIVE)
 * - EXPERIMENTAL/FREEFORM: 0.40 (includes weak rhymes: ASSONANCE 0.50, CONSONANCE 0.40)
 *
 * This allowed EXPERIMENTAL mode to surface weak rhyme labels that would be
 * filtered out in TRADITIONAL mode, while preserving expected behavior for
 * structured songs.
 *
 * NOTE: Distance-based system renders ALL rhymes (no threshold filtering).
 * This function is retained only for backward compatibility.
 *
 * @param stability - Song stability level
 * @returns Minimum weight threshold for rhyme grouping (0.40 or 0.70)
 */
export function getGroupingBoundary(stability: SongStability): number {
  switch (stability) {
    case SongStability.EXPERIMENTAL:
    case SongStability.FREEFORM:
      return 0.40; // Allow ASSONANCE (0.50) and CONSONANCE (0.40)
    default:
      return 0.70; // TRADITIONAL/CONTEMPORARY: strong rhymes only
  }
}

/**
 * @deprecated - Threshold filtering removed in distance-based system
 *
 * Assign letter for rhyme group with callback tracking
 * Uses stability-aware grouping boundary to determine if words rhyme strongly enough
 *
 * Letter assignment logic:
 * - Groups rhymes by category weight >= grouping boundary (varies by stability)
 * - Resets letter sequence on section boundaries
 * - EXCEPTION: prechorus→chorus does NOT reset (callback continuation)
 * - When a rhyme reuses a letter from a prior section, adds subscript (A₁, A₂)
 *
 * NOTE: Distance-based system no longer filters rhymes by threshold.
 * All rhymes always get letters - only opacity varies by distance.
 * This function is retained for backward compatibility.
 *
 * @param categoryWeight - Weight of the rhyme category (0.0-1.0)
 * @param existingGroups - Map of rhyme sounds to letter assignments
 * @param currentSectionIndex - Index of current section
 * @param sectionType - Type of current section (verse, chorus, etc.)
 * @param previousSectionType - Type of previous section (for prechorus→chorus detection)
 */
export function assignLetter(
  categoryWeight: number,
  existingGroups: Map<string, LetterGroup>,
  _currentSectionIndex: number,
  sectionType: string,
  previousSectionType?: string
): { letter: string; callbackSubscript?: number } {
  // Below grouping boundary - no letter assigned (not a strong enough rhyme)
  // NOTE: This check is deprecated - new system always assigns letters
  if (categoryWeight < 0.70) {
    return { letter: '' };
  }

  // Check if this is a prechorus→chorus transition (callback continuation)
  // Note: This check is placeholder for future callback continuation logic
  // For now, we just generate simple letters - full callback tracking
  // would require tracking rhyme groups across sections
  void previousSectionType; // Reserved for future callback continuation
  void sectionType; // Reserved for future section-based logic

  // Generate letter based on group count
  // Use simple A, B, C... sequence
  const letterIndex = existingGroups.size % 26;
  const letter = String.fromCharCode(65 + letterIndex); // A=65 in ASCII

  return { letter };
}

/**
 * @deprecated - Uses legacy categorical opacity (use calculateDistanceBasedOpacity instead)
 *
 * Get complete visual encoding for a rhyme label
 * Combines category, stability, and letter assignment into visual properties
 *
 * Border style based on category strength:
 * - solid: weight >= 0.85 (PERFECT, FAMILY_VOICED)
 * - dashed: weight >= 0.70 (FAMILY_UNVOICED, ADDITIVE, SUBTRACTIVE)
 * - dotted: weight < 0.70 (ASSONANCE, CONSONANCE)
 *
 * Font weight based on weight tier:
 * - 700: weight >= 0.85 (strongest)
 * - 600: weight >= 0.70 (strong)
 * - 500: weight >= 0.50 (medium)
 * - 400: weight < 0.50 (weak)
 *
 * NOTE: This function uses the old categorical opacity system via interpretStability().
 * New code should calculate opacity with calculateDistanceBasedOpacity() and construct
 * VisualEncoding directly.
 *
 * @param category - The rhyme category
 * @param stability - The song's stability level
 * @param letterInfo - Letter and optional callback subscript
 */
export function getVisualEncoding(
  category: RhymeCategory,
  stability: SongStability,
  letterInfo: { letter: string; callbackSubscript?: number }
): VisualEncoding {
  const weight = CATEGORY_WEIGHTS[category];
  const { opacity } = interpretStability(category, stability);

  // Determine border style based on weight
  let borderStyle: 'solid' | 'dashed' | 'dotted';
  if (weight >= 0.85) {
    borderStyle = 'solid';
  } else if (weight >= 0.70) {
    borderStyle = 'dashed';
  } else {
    borderStyle = 'dotted';
  }

  // Determine font weight based on weight tier
  let fontWeight: 400 | 500 | 600 | 700;
  if (weight >= 0.85) {
    fontWeight = 700;
  } else if (weight >= 0.70) {
    fontWeight = 600;
  } else if (weight >= 0.50) {
    fontWeight = 500;
  } else {
    fontWeight = 400;
  }

  // Get color for letter (rotate through color array)
  const letterIndex = letterInfo.letter.charCodeAt(0) - 65; // A=0, B=1, etc.
  const colorToken = LETTER_COLORS[letterIndex % LETTER_COLORS.length];

  return {
    letter: letterInfo.letter,
    callbackSubscript: letterInfo.callbackSubscript,
    borderStyle,
    fontWeight,
    opacity,
    colorToken,
  };
}

/**
 * Get color token for a letter (used for color mapping)
 * Rotates through design token colors
 */
export function getLetterColor(letter: string): string {
  const letterIndex = letter.charCodeAt(0) - 65; // A=0, B=1, etc.
  return LETTER_COLORS[letterIndex % LETTER_COLORS.length];
}

/**
 * Calculate distance-based opacity for rhyme label
 *
 * Formula:
 * - actualDistance <= expectedDistance: opacity = 1.0 (full)
 * - expectedDistance < actualDistance < maxAcceptableDistance: linear interpolation
 * - actualDistance >= maxAcceptableDistance: opacity = minOpacity (floor)
 *
 * @param actualDistance - Actual line distance between rhyming words
 * @param stabilityLevel - Numeric stability level (1-10)
 * @returns Opacity value (clamped between minOpacity and 1.0)
 */
export function calculateDistanceBasedOpacity(
  actualDistance: number,
  stabilityLevel: number
): number {
  // Get config for this stability level (default to level 5 if invalid)
  const config = STABILITY_SCALE[stabilityLevel] || STABILITY_SCALE[5];

  // If at or below expected distance: full opacity
  if (actualDistance <= config.expectedDistance) {
    return 1.0;
  }

  // If beyond max acceptable distance: floor opacity
  if (actualDistance >= config.maxAcceptableDistance) {
    return config.minOpacity;
  }

  // Linear interpolation from 1.0 down to minOpacity
  const distanceRange = config.maxAcceptableDistance - config.expectedDistance;
  const distanceOverExpected = actualDistance - config.expectedDistance;
  const t = distanceOverExpected / distanceRange; // 0.0 to 1.0

  // Interpolate: 1.0 - t * (1.0 - minOpacity)
  // When t=0: opacity = 1.0
  // When t=1: opacity = minOpacity
  const opacity = 1.0 - t * (1.0 - config.minOpacity);

  // Clamp to ensure we stay in valid range
  return Math.max(config.minOpacity, Math.min(1.0, opacity));
}

/**
 * Map numeric rhythmic_stability value (0-1) to numeric stability level (1-10)
 *
 * Linear mapping:
 * - 0.0 → 10 (most loose/experimental)
 * - 0.5 → 5 (medium)
 * - 1.0 → 1 (most tight/traditional)
 *
 * @param rhythmicStability - Numeric rhythmic_stability from prosody_settings (0-1 range)
 * @returns Stability level (1-10), where 1 is tight and 10 is loose
 */
export function mapRhythmicStabilityToLevel(rhythmicStability: number): number {
  // Invert the scale: high rhythmic stability (1.0) = tight rhyme scheme (level 1)
  // low rhythmic stability (0.0) = loose rhyme scheme (level 10)
  const inverted = 1.0 - rhythmicStability;

  // Map 0.0-1.0 to 1-10
  const level = Math.ceil(inverted * 10);

  // Clamp to valid range (1-10)
  return Math.max(1, Math.min(10, level || 1)); // || 1 handles edge case of 0
}

/**
 * Map numeric rhythmic_stability value (0-1) to SongStability enum
 *
 * @deprecated Use mapRhythmicStabilityToLevel() instead for distance-based rendering
 *
 * Boundaries:
 * - 0.0-0.25 → FREEFORM (no rhyme expectations)
 * - 0.25-0.5 → EXPERIMENTAL (welcomes creative rhyming)
 * - 0.5-0.75 → CONTEMPORARY (allows some slant rhymes)
 * - 0.75-1.0 → TRADITIONAL (expects perfect rhymes)
 *
 * @param value - Numeric rhythmic_stability value from prosody_settings (0-1 range)
 */
export function mapRhythmicStabilityToSongStability(value: number): SongStability {
  if (value < 0.25) {
    return SongStability.FREEFORM;
  } else if (value < 0.5) {
    return SongStability.EXPERIMENTAL;
  } else if (value < 0.75) {
    return SongStability.CONTEMPORARY;
  } else {
    return SongStability.TRADITIONAL;
  }
}
